"use client";

import { useMemo } from "react";
import { buildAgentChartRows } from "@/lib/agent/build-agent-chart-data";
import { getDataset } from "@/lib/agent/agent-dataset-cache";
import {
  chartRowsUseNumericX,
  computeChartYDomain,
  normalizeAgentChartSpec,
} from "@/lib/agent/chart-axis-utils";
import type { AgentChartInput } from "@/lib/agent/validate-chart-spec";
import { resolveChartSeriesKeys } from "@/lib/agent/validate-chart-spec";

export const AGENT_CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

export function useAgentChartModel(spec: AgentChartInput | null) {
  const normalizedSpec = useMemo(() => {
    if (!spec?.datasetId) return null;
    const ds = getDataset(spec.datasetId);
    if (!ds) return spec;
    return normalizeAgentChartSpec(ds.columns, spec);
  }, [spec]);

  const datasetMissing = useMemo(() => {
    if (!normalizedSpec?.datasetId) return false;
    return !getDataset(normalizedSpec.datasetId);
  }, [normalizedSpec?.datasetId]);

  const seriesKeys = useMemo(() => {
    if (!normalizedSpec) return [];
    const ds = getDataset(normalizedSpec.datasetId);
    if (!ds) return normalizedSpec.seriesKeys ?? [];
    return resolveChartSeriesKeys(ds, {
      chartType: normalizedSpec.chartType,
      labelKey: normalizedSpec.labelKey,
      xKey: normalizedSpec.xKey ?? normalizedSpec.labelKey,
      seriesKeys: normalizedSpec.seriesKeys,
    });
  }, [normalizedSpec]);

  const chartData = useMemo(
    () =>
      normalizedSpec ? buildAgentChartRows(normalizedSpec, seriesKeys) : [],
    [normalizedSpec, seriesKeys],
  );

  const chartConfig = useMemo(() => {
    const cfg: Record<string, { label: string; color?: string }> = {};
    if (seriesKeys.length > 0) {
      const ds = normalizedSpec ? getDataset(normalizedSpec.datasetId) : undefined;
      const columnLabels = ds?.meta?.columnLabels as
        | Record<string, string>
        | undefined;
      seriesKeys.forEach((key, i) => {
        cfg[key] = {
          label: columnLabels?.[key] ?? key,
          color: AGENT_CHART_COLORS[i % AGENT_CHART_COLORS.length],
        };
      });
    } else if (normalizedSpec?.chartType === "pie") {
      chartData.forEach((row, i) => {
        cfg[row.label] = {
          label: row.label,
          color: AGENT_CHART_COLORS[i % AGENT_CHART_COLORS.length],
        };
      });
    } else {
      cfg.value = {
        label: normalizedSpec?.valueKey ?? "value",
        color: "hsl(var(--primary))",
      };
    }
    return cfg;
  }, [normalizedSpec, seriesKeys, chartData]);

  const isMultiLine =
    normalizedSpec?.chartType === "line" && seriesKeys.length > 0;
  const wantsTimeX = normalizedSpec?.xAxis === "time";
  const isNumericTimeX = wantsTimeX && chartRowsUseNumericX(chartData);

  const seriesYAxisId = useMemo(() => {
    const map = new Map<string, "left" | "right">();
    if (!isMultiLine || seriesKeys.length === 0) return map;

    if (normalizedSpec?.seriesScale) {
      for (const key of seriesKeys) {
        const side = normalizedSpec.seriesScale[key];
        if (side) map.set(key, side);
      }
    }

    if (normalizedSpec?.yAxis === "dual" && seriesKeys.length >= 2) {
      const ranges = seriesKeys.map((key) => {
        const vals = chartData
          .map((r) => r[key])
          .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
        if (!vals.length) return { key, span: 0 };
        return { key, span: Math.max(...vals) - Math.min(...vals) };
      });
      const maxSpan = Math.max(...ranges.map((r) => r.span), 0);
      ranges.forEach((r, i) => {
        if (!map.has(r.key)) {
          map.set(r.key, i === 0 ? "left" : "right");
        }
      });
      if (maxSpan > 0) {
        const sorted = [...ranges].sort((a, b) => b.span - a.span);
        if (sorted[0] && sorted[1] && sorted[0].span > sorted[1].span * 10) {
          map.set(sorted[0].key, "right");
          map.set(sorted[1].key, "left");
        }
      }
    }

    for (const key of seriesKeys) {
      if (!map.has(key)) map.set(key, "left");
    }
    return map;
  }, [chartData, isMultiLine, seriesKeys, normalizedSpec?.seriesScale, normalizedSpec?.yAxis]);

  const useDualAxis =
    isMultiLine &&
    new Set([...seriesYAxisId.values()]).size > 1;

  const yDomainLeft = useMemo(
    () =>
      computeChartYDomain(
        chartData,
        isMultiLine
          ? seriesKeys.filter((k) => seriesYAxisId.get(k) !== "right")
          : ["value"],
      ),
    [chartData, isMultiLine, seriesKeys, seriesYAxisId],
  );

  const yDomainRight = useMemo(
    () =>
      useDualAxis
        ? computeChartYDomain(
            chartData,
            seriesKeys.filter((k) => seriesYAxisId.get(k) === "right"),
          )
        : undefined,
    [chartData, useDualAxis, seriesKeys, seriesYAxisId],
  );

  const yDomain = yDomainLeft;

  const heightClass =
    normalizedSpec?.chartType === "pie"
      ? "h-56"
      : isMultiLine
        ? "h-52"
        : "h-44";

  return {
    spec: normalizedSpec,
    seriesKeys,
    chartData,
    chartConfig,
    isMultiLine,
    isNumericTimeX,
    yDomain,
    yDomainRight,
    useDualAxis,
    seriesYAxisId,
    datasetMissing,
    heightClass,
    hasRenderableChart:
      !!normalizedSpec && chartData.length > 0 && !datasetMissing,
  };
}

export type AgentChartModel = ReturnType<typeof useAgentChartModel>;
